import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Minus, Compass, Locate, Mountain,
  ChevronUp, ChevronDown,
} from 'lucide-react';
import { MAP_DEFAULT_ZOOM } from './config/mapConfig';
import { CONTRIBUTION_SHEET_BOTTOM, MAP_SHEET_BOTTOM } from './mapSheetOffset';
import { useMapStore } from '../../stores/mapStore';
import { mapPositionToCoordinates } from '../../utils/geolocation';
import { requestCurrentPosition } from '../../utils/geolocationRequest';
import { openLocationPrompt } from '../../utils/locationPrompt';

export type MapStyle = 'standard' | 'dark' | 'satellite' | 'outdoors' | 'navigation';

interface MapControlsFABProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onToggleTilt?: () => void;
  isTilted?: boolean;
  bearing: number;
  is3DEnabled?: boolean;
  onToggle3D?: (enabled: boolean) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
  /** When true, pin near the safe area instead of lifting for the home bottom sheet (campaign-only layout). */
  compactBottom?: boolean;
  /** Extra bottom offset (e.g. campaign session dock above the sheet). */
  extraBottomOffset?: string;
}

export function MapControlsFAB({
  onZoomIn,
  onZoomOut,
  onToggleTilt,
  isTilted = false,
  bearing,
  is3DEnabled = false,
  onToggle3D,
  isExpanded,
  onToggleExpand,
  compactBottom = false,
  extraBottomOffset = '0px',
}: MapControlsFABProps) {
  const {
    setUserLocation,
    setIsLocating,
    isLocating,
    flyToLocation,
    sheetState,
    contributionSheetSnap,
    contributionSheetChromeBottom,
  } = useMapStore();

  const contributionChromeActive = contributionSheetSnap != null;
  const measuredChromeBottom = contributionSheetChromeBottom;
  const overlaySheetActive = contributionChromeActive || measuredChromeBottom != null;

  // Dynamic bottom position to stay above bottom sheet with enough clearance
  const exploreSheetBottom: Record<string, string> = {
    minimized: '2rem',
    collapsed: 'calc(8vh + 1.5rem)',
    half: 'calc(40vh + 1.5rem)',
    expanded: 'calc(85vh + 1.5rem)',
  };
  const contributionBottom =
    measuredChromeBottom
    ?? (contributionSheetSnap ? CONTRIBUTION_SHEET_BOTTOM[contributionSheetSnap] : MAP_SHEET_BOTTOM.half);
  const sheetBottom = compactBottom
    ? '1.25rem'
    : overlaySheetActive
      ? contributionBottom
      : (exploreSheetBottom[sheetState] || '2rem');
  const controlsBottom =
    extraBottomOffset && extraBottomOffset !== '0px'
      ? `calc(${sheetBottom} + ${extraBottomOffset})`
      : sheetBottom;

  const handleLocate = () => {
    if (!navigator.geolocation) {
      openLocationPrompt();
      return;
    }

    setIsLocating(true);

    requestCurrentPosition()
      .then((position) => {
        const location = mapPositionToCoordinates(position);

        setUserLocation(location);
        flyToLocation(location, MAP_DEFAULT_ZOOM);
        setIsLocating(false);
      })
      .catch((error: GeolocationPositionError) => {
        console.error('Geolocation error:', error);
        setIsLocating(false);
        openLocationPrompt();
      });
  };


  return (
    <div
      className={`fixed right-3 flex flex-col items-end gap-2 transition-all duration-300 ${overlaySheetActive ? 'z-[70]' : 'z-[55]'}`}
      style={{
        bottom: controlsBottom,
        transition: 'bottom 350ms cubic-bezier(0.32, 0.72, 0, 1)',
      }}
    >
      {/* Expandable controls */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="flex flex-col items-end gap-2"
          >
            {/* 2D/3D Toggle - on top of zoom */}
            {onToggle3D && (
              <motion.button
                data-tour-target="3d-toggle"
                onClick={() => onToggle3D(!is3DEnabled)}
                className={`w-14 h-14 md:w-12 md:h-12 rounded-xl shadow-lg dark:shadow-black/40 flex items-center justify-center transition-all ${is3DEnabled
                  ? 'bg-primary-50 text-primary-600 ring-2 ring-primary-500/50 dark:bg-gray-900/90 dark:backdrop-blur-md dark:text-primary-400 dark:ring-primary-500/80 dark:ring-offset-0'
                  : 'bg-white dark:bg-gray-900/95 dark:backdrop-blur-xl dark:ring-1 dark:ring-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/80'
                  }`}
                whileTap={{ scale: 0.95 }}
                aria-label={is3DEnabled ? 'Switch to 2D' : 'Switch to 3D'}
              >
                <span className="text-xs font-bold">{is3DEnabled ? '3D' : '2D'}</span>
              </motion.button>
            )}

            {/* Zoom Controls */}
            <div data-tour-target="zoom" className="bg-white dark:bg-gray-900/95 dark:backdrop-blur-xl dark:ring-1 dark:ring-gray-700/50 rounded-xl shadow-lg dark:shadow-black/40 overflow-hidden">
              <button
                onClick={onZoomIn}
                className="w-14 h-14 md:w-12 md:h-12 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-b border-gray-100 dark:border-gray-700/50"
                aria-label="Zoom in"
              >
                <Plus size={22} className="text-gray-700 dark:text-gray-300" />
              </button>
              <button
                onClick={onZoomOut}
                className="w-14 h-14 md:w-12 md:h-12 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                aria-label="Zoom out"
              >
                <Minus size={22} className="text-gray-700 dark:text-gray-300" />
              </button>
            </div>

            {/* Tilt + Compass Toggle */}
            <motion.button
              data-tour-target="tilt"
              onClick={onToggleTilt}
              className={`w-14 h-14 md:w-12 md:h-12 rounded-xl shadow-lg dark:shadow-black/40 flex items-center justify-center transition-all ${isTilted
                ? 'bg-primary-50 text-primary-600 ring-2 ring-primary-500/50 dark:bg-gray-900/90 dark:backdrop-blur-md dark:text-primary-400 dark:ring-primary-500/80 dark:ring-offset-0'
                : 'bg-white dark:bg-gray-900/95 dark:backdrop-blur-xl dark:ring-1 dark:ring-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/80'
                }`}
              whileTap={{ scale: 0.95 }}
              aria-label={isTilted ? 'Reset view' : 'Tilt map'}
            >
              <motion.div animate={{ rotate: isTilted ? -bearing : 0 }} transition={{ type: 'spring', damping: 20, stiffness: 200 }}>
                {isTilted ? <Compass size={24} /> : <Mountain size={22} />}
              </motion.div>
            </motion.button>

            {/* My Location */}
            <motion.button
              data-tour-target="locate"
              onClick={handleLocate}
              disabled={isLocating}
              className="w-14 h-14 md:w-12 md:h-12 bg-white dark:bg-gray-900/95 dark:backdrop-blur-xl dark:ring-1 dark:ring-gray-700/50 rounded-xl shadow-lg dark:shadow-black/40 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-800/80 transition-colors disabled:opacity-50"
              whileTap={{ scale: 0.95 }}
              aria-label="Get my location"
            >
              {isLocating ? (
                <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Locate size={22} className="text-gray-700 dark:text-gray-300" />
              )}
            </motion.button>

          </motion.div>
        )}
      </AnimatePresence>

      {/* Expand toggle */}
      <motion.button
        data-tour-target="controls-toggle"
        onClick={onToggleExpand}
        className="w-14 h-14 md:w-12 md:h-12 bg-white dark:bg-gray-900/95 dark:backdrop-blur-xl dark:ring-1 dark:ring-gray-700/50 rounded-xl shadow-lg dark:shadow-black/40 flex items-center justify-center text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/80 transition-colors shrink-0"
        whileTap={{ scale: 0.95 }}
        aria-label={isExpanded ? 'Hide map controls' : 'Show map controls'}
      >
        {isExpanded ? <ChevronDown size={22} /> : <ChevronUp size={22} />}
      </motion.button>
    </div>
  );
}
